import React, { useState, useEffect } from 'react';
import { useRole, BACKEND_URL } from '../App';
import {
  StatusBadge, TypePill, TriggerPill, GridSkeleton, EmptyState,
  NotionBlockRenderer, PageHeader
} from '../components/shared';
import { GuidedProcedureView } from '../components/GuidedProcedure';
import { Search, X, FileText, ChevronLeft, ChevronDown, Filter, User } from 'lucide-react';

// Function group display order and icons
const FUNCTION_ORDER = [
  'Client Care & Communication',
  'Booking & Scheduling Control',
  'Transport & Logistics',
  'Aquacremation Operations',
  'Cleaning & Maintenance',
  'Finance & Compliance',
  'Equipment & Facilities',
  'Marketing & Digital Systems',
  'Human Resources & Training',
  'Inventory & Supplies',
];

export default function ProceduresLibrary() {
  const { role } = useRole();
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showMyOnly, setShowMyOnly] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { 
      const saved = localStorage.getItem('ps_proc_groups');
      if (saved) return JSON.parse(saved);
      // Default: all collapsed
      const defaults = {};
      FUNCTION_ORDER.forEach(f => { defaults[f] = true; });
      defaults['Other'] = true;
      return defaults;
    } catch { return {}; }
  });
  
  // Side panel
  const [selectedProc, setSelectedProc] = useState(null);
  const [procDetail, setProcDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subpageContent, setSubpageContent] = useState({});
  const [expandedSubpage, setExpandedSubpage] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/procedures`)
      .then(r => r.json())
      .then(data => {
        setProcedures(data.procedures || []);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  // Filter procedures
  const filteredProcedures = procedures.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && 
        !(p.purpose && p.purpose.toLowerCase().includes(search.toLowerCase()))) return false;
    if (showMyOnly && !p.owning_role) return false;
    return true;
  });

  // Group by function
  const groupedByFunction = {};
  const ungrouped = [];
  
  filteredProcedures.forEach(p => {
    if (p.functions && p.functions.length > 0) {
      const primaryFunc = p.functions[0]; // Use first function
      if (!groupedByFunction[primaryFunc]) groupedByFunction[primaryFunc] = [];
      groupedByFunction[primaryFunc].push(p);
    } else {
      ungrouped.push(p);
    }
  });

  // Sort groups by defined order
  const sortedGroups = FUNCTION_ORDER
    .filter(f => groupedByFunction[f] && groupedByFunction[f].length > 0)
    .map(f => ({ name: f, procedures: groupedByFunction[f] }));
  
  // Add any functions not in the order
  Object.keys(groupedByFunction).forEach(f => {
    if (!FUNCTION_ORDER.includes(f)) {
      sortedGroups.push({ name: f, procedures: groupedByFunction[f] });
    }
  });
  
  if (ungrouped.length > 0) {
    sortedGroups.push({ name: 'Other', procedures: ungrouped });
  }

  const openProcedure = async (proc) => {
    setSelectedProc(proc);
    setDetailLoading(true);
    setProcDetail(null);
    setExpandedSubpage(null);
    setSubpageContent({});
    try {
      const res = await fetch(`${BACKEND_URL}/api/procedures/${proc.id}`);
      const data = await res.json();
      setProcDetail(data);
    } catch { }
    setDetailLoading(false);
  };

  const loadSubpage = async (procId, subpageId) => {
    if (expandedSubpage === subpageId) { setExpandedSubpage(null); return; }
    setExpandedSubpage(subpageId);
    if (subpageContent[subpageId]) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/procedures/${procId}/subpage/${subpageId}`);
      const data = await res.json();
      setSubpageContent(prev => ({ ...prev, [subpageId]: data.blocks }));
    } catch { }
  };

  const getProcNumber = (name) => {
    const match = name.match(/^P\d+/);
    return match ? match[0] : 'P??';
  };

  const getProcTitle = (name) => {
    return name.replace(/^P\d+\s*[\u2013\u2014-]\s*/, '').trim() || name;
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [groupName]: !prev[groupName] };
      localStorage.setItem('ps_proc_groups', JSON.stringify(next));
      return next;
    });
  };

  return (
    <div className="page-container" data-testid="procedures-library">
      <PageHeader title="Procedures Library" subtitle={`${filteredProcedures.length} procedures`} />

      {/* Search + Toggle */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search procedures..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="procedures-search-input"
          />
        </div>
        <button
          onClick={() => setShowMyOnly(!showMyOnly)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
            showMyOnly 
              ? 'bg-primary text-primary-foreground border-primary' 
              : 'border-input hover:bg-muted text-muted-foreground'
          }`}
          data-testid="procedures-my-toggle"
        >
          <User size={16} />
          My Procedures
        </button>
      </div>

      {/* Grouped Procedures */}
      {loading ? <GridSkeleton /> : sortedGroups.length > 0 ? (
        <div className="space-y-4">
          {sortedGroups.map(group => {
            const isCollapsed = collapsedGroups[group.name];
            return (
            <section key={group.name} className="ps-card-static overflow-hidden">
              <button onClick={() => toggleGroup(group.name)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors">
                <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-wider flex-1">{group.name}</h2>
                <span className="text-xs text-muted-foreground">{group.procedures.length}</span>
                <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`} />
              </button>
              {!isCollapsed && (
              <div className="px-4 pb-3">
                <div className="space-y-1">
                {group.procedures.map(proc => (
                  <button
                    key={proc.id}
                    className="w-full text-left ps-card p-4 flex items-start gap-4"
                    onClick={() => openProcedure(proc)}
                    data-testid={`procedure-card-${proc.id}`}
                  >
                    <span className="font-mono text-xs bg-muted px-2.5 py-1 rounded-full font-semibold flex-shrink-0 mt-0.5">
                      {getProcNumber(proc.name)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold">{getProcTitle(proc.name)}</h3>
                      {proc.purpose && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">{proc.purpose}</p>
                      )}
                    </div>
                    {proc.owning_role && (
                      <span className="text-xs text-muted-foreground flex-shrink-0 hidden sm:block">{proc.owning_role}</span>
                    )}
                  </button>
                ))}
                </div>
              </div>
              )}
            </section>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={FileText}
          title="No procedures found"
          description={search ? "Try adjusting your search." : "No procedures available."}
          action={search ? { label: 'Clear search', onClick: () => setSearch('') } : undefined}
        />
      )}

      {/* Side Panel */}
      {selectedProc && (
        <>
          <div className={`side-panel-overlay ${selectedProc ? 'open' : ''}`} onClick={() => setSelectedProc(null)} />
          <div className={`side-panel ${selectedProc ? 'open' : ''}`} data-testid="procedure-detail-panel">
            <div className="sticky top-0 bg-card z-10 p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <button onClick={() => setSelectedProc(null)} className="p-2 rounded-lg hover:bg-muted" data-testid="panel-close-button">
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className="mt-4">
                <span className="font-mono text-xs bg-muted px-2.5 py-1 rounded-full font-semibold">
                  {getProcNumber(selectedProc.name)}
                </span>
                <h2 className="text-lg font-bold mt-2">
                  {getProcTitle(selectedProc.name)}
                </h2>
                {selectedProc.owning_role && (
                  <p className="text-xs text-muted-foreground mt-1">{selectedProc.owning_role}</p>
                )}
              </div>
            </div>
            <div className="p-6">
              {detailLoading ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-muted rounded animate-skeleton" style={{ width: `${70 + Math.random() * 30}%` }} />)}
                </div>
              ) : procDetail ? (
                <>
                  {/* Guided Procedure View */}
                  {procDetail.guided ? (
                    <GuidedProcedureView 
                      guided={procDetail.guided} 
                      blocks={procDetail.blocks} 
                    />
                  ) : (
                    <NotionBlockRenderer blocks={procDetail.blocks} />
                  )}
                  {procDetail.subpages?.length > 0 && (
                    <div className="mt-8 border-t border-border pt-6">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Related Pages</h3>
                      {procDetail.subpages.map(sp => (
                        <div key={sp.id} className="mb-2">
                          <button
                            onClick={() => loadSubpage(selectedProc.id, sp.id)}
                            className="w-full text-left ps-card-static p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                          >
                            <span className="text-sm font-medium">{sp.title}</span>
                            <ChevronDown size={16} className={`transition-transform duration-200 ${expandedSubpage === sp.id ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedSubpage === sp.id && subpageContent[sp.id] && (
                            <div className="pl-4 pt-4 border-l-2 border-primary/20 ml-4">
                              <NotionBlockRenderer blocks={subpageContent[sp.id]} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
